import {csrfToken} from './api';
import type {
  AttorneyPackage,
  DocumentCategory,
  EvidenceRelevance,
  EvidenceRequest,
  EvidenceRequestStatus,
  FenzSafeAdapter,
  FenzSafeDocument,
  FenzSafeLoadError,
  FenzSafeMatterCreationSummary,
  FenzSafeRepresentationStatus,
  FenzSafeState,
  FenzSafeUserRole,
  Matter,
  MatterTier,
  MatterWatchSubscription,
  MatterWatchTriggerType,
  OrganizeSuggestion,
  TimelineEntry,
  TriageState,
} from './fenzsafeStore';

/**
 * Deliberately not imported from fenzsafeStore.ts (which defines its own identical class): this
 * adapter has no runtime dependency on fenzsafeStore.ts at all, only type-only ones (erased at
 * build time). Importing the class itself would create a real module cycle the moment a test
 * swaps `fenzsafeStore`'s own export for this adapter via `vi.mock('./fenzsafeStore', ...)` --
 * fenzsafeBackendAdapter.ts importing a value from fenzsafeStore.ts while a mock factory for
 * fenzsafeStore.ts is simultaneously constructing its replacement export from this very module
 * deadlocks the module graph. `friendlyMutationError.ts` (the one consumer that treats conflict
 * errors specially) matches purely on `error.name === 'FenzSafeConflictError'`, not `instanceof`,
 * so a same-named local class is fully equivalent for every real caller.
 */
class FenzSafeConflictError extends Error {
  constructor(
    message: string,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'FenzSafeConflictError';
  }
}

/**
 * Real backend implementation of FenzSafeAdapter, calling the HTTP contract documented in
 * docs/ai/FENZSAFE_HTTP_INTEGRATION.md (MatterController/DocumentController/
 * AttorneyPackageController -- chunks 5-7). This is the harness for Priority 4 of the
 * 2026-09-12 checkpoint instruction: proving the existing FenzSafe screens can point at the
 * real backend contracts without any component edits, by swapping which adapter the module
 * binding resolves to (see fenzsafeBackendAdapter.test.ts) rather than changing FenzSafeApp.tsx/
 * FenzSafeAttorneyView.tsx/FenzSafeWalkthrough.tsx.
 *
 * Three real gaps surfaced while building this, deliberately not papered over:
 *
 * 1. **RESOLVED (Issue #114 Task 2, comment 5726304682).** A real, durable, tenant/matter-scoped
 *    timeline endpoint now exists (`GET /api/v1/fenzsafe/matters/{id}/timeline`, backed by
 *    `FenzSafeMatterTimelineReader`, reusing the canonical ProFenz audit system -- see
 *    docs/ai/FENZSAFE_DURABLE_TIMELINE_AUDIT.md for the full design record). `composeMatter()`
 *    below fetches it fresh on every matter load/refresh via `fetchTimeline()`, replacing the
 *    session-local reconstruction this comment used to describe (matter creation from
 *    `created_at` plus one client-fabricated entry per mutation, lost on reload). The one entry
 *    type this real timeline cannot show is `simulateAttorneyOpen()`'s own client-only status
 *    flip below, which still has no real backend event behind it -- see that method's own
 *    comment for why, unchanged from before.
 * 2. **Most mutations still don't accept an idempotency key -- only tier upgrades and matter
 *    creation do.** `MatterRepository::createIdempotently()` now mirrors ProFenz's real
 *    `OrderDraftRepository::createIdempotently()` convention (INSERT IGNORE + re-SELECT on a
 *    unique `(organization_id, created_by_identity_id, idempotency_key_hash)` key, per Chat's
 *    decision that FenzSafe should reuse the established idempotency scheme rather than a
 *    parallel one), so `createMatter()` below actually sends `idempotency_key`. `rename/archive/
 *    restore/updateNotificationPreference` and `AttorneyPackageRepository::create/revoke` (see
 *    their interface signatures) still take no idempotency parameter at all. The `FenzSafeAdapter`
 *    interface threads an `idempotencyKey` through every mutation; this adapter still accepts the
 *    parameter (to satisfy the interface every screen already calls against) but only actually
 *    sends it where the backend has somewhere to put it. A retried request on a dropped connection
 *    is not yet safe to resend blindly for the other mutations -- a real gap for Codex, not solved
 *    by inventing idempotency handling the backend doesn't have.
 * 3. **`DocumentController::update()` cannot rename a document.** It only accepts
 *    `category`/`triage_state`/`evidence_relevance` (see its `update()` method) -- renaming
 *    only happens by uploading a new version (`addDocumentVersion`). `FenzSafeAdapter.
 *    updateDocument()`'s patch type allows `name`, matching the synthetic store's broader
 *    (and more permissive) behavior. This adapter silently drops a `name` patch field rather
 *    than fabricating rename support the backend doesn't have; screens that only ever patch
 *    category/triageState/evidenceRelevance (the current FenzSafe UI) are unaffected.
 * 4. **Documents are not part of the Matter aggregate's optimistic-concurrency version.** The
 *    synthetic store treats `Matter.documents` as embedded, so adding/changing a document also
 *    advances `Matter.version` (see `setMatter()` in fenzsafeStore.ts). The real backend scopes
 *    documents as their own independently-versioned aggregate (`FenzSafeDocumentRepository`,
 *    each `FenzSafeDocument` carrying its own `version`) that never touches the matter's version
 *    -- `DocumentController::add()` doesn't even accept an `expected_version` (see its
 *    signature). This adapter honors the real backend's aggregate boundary: `addDocument()`'s
 *    `expectedVersion` parameter is accepted (to satisfy the interface) but not sent, and the
 *    parent matter's own `version` field is left untouched by any document-only mutation.
 */

class FenzSafeBackendError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'FenzSafeBackendError';
  }
}

type WireMatter = {
  matter_id: string;
  organization_id: string;
  name: string;
  reference: string;
  tier: MatterTier;
  archived: boolean;
  notify_on_attorney_open: boolean;
  user_role: FenzSafeUserRole | null;
  representation_status?: FenzSafeRepresentationStatus | null;
  version: number;
  created_at: string;
};

type WireDocument = {
  document_id: string;
  matter_id: string;
  name: string;
  category: DocumentCategory;
  triage_state: TriageState;
  processing_status: FenzSafeDocument['processingStatus'];
  size_kb: number;
  added_at: string;
  source: 'upload' | 'phone_import' | 'desktop_discovery';
  evidence_relevance: EvidenceRelevance | null;
  phone_source: string | null;
  superseded_by: string | null;
  doc_version: number;
  version: number;
  replaces_document_id: string | null;
  content_sha256: string | null;
};

type WireAttorneyPackageSnapshotDocument = {
  document_id: string;
  name: string;
  category: string;
  added_at: string;
};

type WireAttorneyPackageSnapshot = {
  matter_name: string;
  matter_reference: string;
  created_at: string;
  documents: WireAttorneyPackageSnapshotDocument[];
};

type WireAttorneyPackage = {
  matter_id: string;
  status: AttorneyPackage['status'];
  share_token: string;
  created_at: string;
  opened_at: string | null;
  revoked_at: string | null;
  version: number;
  snapshot: WireAttorneyPackageSnapshot | null;
};

type WireEvidenceRequest = {
  request_id: string;
  description: string;
  status: EvidenceRequestStatus;
  fulfilled_document_id: string | null;
  decline_reason: string | null;
  created_at: string;
  resolved_at: string | null;
  version: number;
};

type WireMatterWatchSubscription = {
  matter_id: string;
  organization_id: string;
  party_names: string[];
  court: string;
  county: string;
  case_type: string | null;
  case_number: string | null;
  trigger_types: MatterWatchTriggerType[];
  enabled: boolean;
  created_at: string;
  version: number;
};

type WireOrganizeSuggestion = {
  document_id: string;
  current_category: string;
  suggested_category: string;
  reason: string;
};

type WireTimelineEntry = {
  event_id: string;
  event_type: string;
  occurred_at: string;
  reason: string;
  metadata: Record<string, unknown>;
};

/**
 * Plain-human labels for the real event types MatterController/DocumentController/
 * AttorneyPackageController emit (Issue #114 Task 2) -- never a raw action code in the UI. Falls
 * back to the event's own server-written `reason` (already a full sentence, see e.g.
 * MatterController::recordTimelineAudit()'s callers) for any event type not listed here, so a
 * future event type this adapter hasn't been updated for yet still renders something readable
 * rather than a blank or a raw code.
 */
function timelineLabelFromWire(entry: WireTimelineEntry): {label: string; detail?: string} {
  const documentName = typeof entry.metadata.document_name === 'string' ? entry.metadata.document_name : undefined;
  switch (entry.event_type) {
    case 'fenzsafe_matter.created':
      return {label: 'Matter created'};
    case 'fenzsafe_matter.renamed':
      return {label: 'Matter name/reference updated'};
    case 'fenzsafe_matter.archived':
      return {label: 'Matter archived'};
    case 'fenzsafe_matter.restored':
      return {label: 'Matter restored from archive'};
    case 'fenzsafe_matter.notification_preference_updated':
      return {label: entry.metadata.notify_on_attorney_open ? 'Turned on attorney-open email notifications' : 'Turned off attorney-open email notifications'};
    case 'fenzsafe_matter.tier_upgraded':
      return {label: `Upgraded to ${entry.metadata.to_tier === 'one_time' ? 'one-time prep pass' : 'monthly case workspace'}`};
    case 'fenzsafe_document.added':
      return {label: documentName ? `${documentName} added` : 'Document added'};
    case 'fenzsafe_document.version_added':
      return {label: documentName ? `${documentName} added as a new version` : 'A new document version was added'};
    case 'fenzsafe_document.upload_accepted':
      return {label: documentName ? `${documentName} uploaded and passed the security scan` : 'A document passed the security scan'};
    case 'fenzsafe_document.upload_quarantined':
      return {label: documentName ? `${documentName} failed the security scan` : 'A document failed the security scan'};
    case 'fenzsafe_document.updated':
      return {label: 'Document updated'};
    case 'fenzsafe_document.organize_suggestions_completed':
      return {label: 'Document-organization suggestions reviewed', detail: organizeSuggestionsCountDetail(entry)};
    case 'fenzsafe_document.removed':
      return {label: documentName ? `${documentName} removed` : 'Document removed'};
    case 'fenzsafe_attorney_package.share_created':
      return {label: 'Attorney package created and share link generated', detail: sharedDocumentCountDetail(entry)};
    case 'fenzsafe_attorney_package.share_updated':
      return {label: 'Shared package updated', detail: sharedDocumentCountDetail(entry)};
    case 'fenzsafe_attorney_package.share_revoked':
      return {label: 'Attorney package access revoked'};
    case 'fenzsafe_attorney_package.share_link_viewed':
      return {label: 'Attorney opened the shared package'};
    case 'fenzsafe_attorney_package.share_document_downloaded':
      return {label: 'Attorney accessed a shared document'};
    default:
      return {label: entry.reason};
  }
}

function sharedDocumentCountDetail(entry: WireTimelineEntry): string | undefined {
  const count = entry.metadata.shared_document_count;
  return typeof count === 'number' ? `${count} document${count === 1 ? '' : 's'} shared` : undefined;
}

function organizeSuggestionsCountDetail(entry: WireTimelineEntry): string | undefined {
  const {accepted_count: accepted, edited_count: edited, skipped_count: skipped} = entry.metadata;
  if (typeof accepted !== 'number' || typeof edited !== 'number' || typeof skipped !== 'number') return undefined;
  return `${accepted} accepted, ${edited} edited, ${skipped} skipped`;
}

function timelineEntryFromWire(entry: WireTimelineEntry): TimelineEntry {
  const {label, detail} = timelineLabelFromWire(entry);
  return {id: entry.event_id, label, detail, at: entry.occurred_at};
}

/**
 * Empty/loading/error states here must never erase the underlying matter workflow (Issue #114
 * Task 2, comment 5726304682's UI requirements): a real matter (documents, attorney package,
 * plan) still has to load and stay usable even if the timeline itself can't be fetched -- e.g.
 * a 503 from `MatterController::timeline()` when no reader is wired in this environment, or a
 * plain network failure. Both degrade to an empty timeline rather than throwing out of
 * `composeMatter()` and taking the whole matter down with them.
 */
async function fetchTimeline(matterId: string): Promise<TimelineEntry[]> {
  try {
    const body = await request(`/api/v1/fenzsafe/matters/${matterId}/timeline`, 'GET');
    return (body.timeline as WireTimelineEntry[]).map(timelineEntryFromWire);
  } catch {
    return [];
  }
}

interface WirePricingPlan {
  plan_key: string;
  display_price_minor: number | null;
  currency_code: string | null;
  has_active_offer: boolean;
}

/**
 * Sources the amount/currency for a tier upgrade from the admin-governed pricing contract
 * (Issue #164 founder correction, comment 5770794140: "FenzSafe pricing/tiers/offers/entitlements
 * must be admin-configurable, never hard-coded") rather than a client-chosen literal -- this
 * replaces an earlier version of upgradeTier() below that always sent amount_minor: 0 regardless
 * of the requested tier, which meant a paid-tier upgrade could never actually succeed (the
 * backend rejects a paid tier with a zero amount) and, had it not been rejected, would have let
 * the client dictate its own price. The free tier is the one case with no governed lookup needed:
 * downgrading to it is always a zero-amount, no-charge operation.
 */
async function activePricingPlanTerms(tier: MatterTier): Promise<{amountMinor: number; currencyCode: string}> {
  if (tier === 'free') {
    return {amountMinor: 0, currencyCode: 'USD'};
  }
  const body = await request('/api/v1/fenzsafe/pricing-plans', 'GET');
  const plan = (body.plans as WirePricingPlan[]).find((candidate) => candidate.plan_key === tier);
  if (!plan || !plan.has_active_offer || plan.display_price_minor === null || plan.currency_code === null) {
    throw new FenzSafeBackendError('fenzsafe_pricing.not_available', 'This plan is not yet available to upgrade to.');
  }
  return {amountMinor: plan.display_price_minor, currencyCode: plan.currency_code};
}

function scopeHeader(expectedScope?: string): Record<string, string> {
  return expectedScope === undefined ? {} : {'X-ProFenz-Expected-Scope': expectedScope};
}

/** Per-transfer closure: no global scope override and no fallback to a newly selected account. */
export function guestClaimBackendAdapter(expectedScope: string): Pick<FenzSafeAdapter, 'getSnapshot' | 'createMatter' | 'addDocument' | 'registerDocumentBytes'> {
  if (!expectedScope) throw new Error('Reload your account before saving guest work.');
  return {
    getSnapshot: fenzsafeBackendAdapter.getSnapshot,
    createMatter: (...args) => fenzsafeBackendAdapter.createMatter(...args, expectedScope),
    addDocument: (...args) => fenzsafeBackendAdapter.addDocument(...args, expectedScope),
    registerDocumentBytes: (...args) => fenzsafeBackendAdapter.registerDocumentBytes(...args, expectedScope),
  };
}

async function request(path: string, method: string, body?: Record<string, unknown>, expectedScope?: string): Promise<Record<string, unknown>> {
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {'Accept': 'application/json', 'Content-Type': 'application/json', 'X-ProFenz-CSRF': csrfToken(), ...scopeHeader(expectedScope)},
    body: body ? JSON.stringify(body) : undefined,
  });
  const parsed = (await response.json()) as Record<string, unknown>;
  if (!response.ok) {
    const code = typeof parsed.code === 'string' ? parsed.code : 'fenzsafe.unknown_error';
    const message = typeof parsed.message === 'string' ? parsed.message : 'The request could not be completed.';
    if (code.endsWith('.conflict') || code.endsWith('.superseded')) {
      throw new FenzSafeConflictError(message, code);
    }
    throw new FenzSafeBackendError(code, message, response.status);
  }
  return parsed;
}

function documentFromWire(wire: WireDocument): FenzSafeDocument {
  return {
    id: wire.document_id,
    name: wire.name,
    category: wire.category,
    triageState: wire.triage_state,
    processingStatus: wire.processing_status,
    sizeKb: wire.size_kb,
    addedAt: wire.added_at,
    source: wire.source,
    evidenceRelevance: wire.evidence_relevance ?? undefined,
    phoneSource: wire.phone_source ?? undefined,
    supersededBy: wire.superseded_by ?? undefined,
    docVersion: wire.doc_version,
    rowVersion: wire.version,
    replacesDocumentId: wire.replaces_document_id ?? undefined,
    contentSha256: wire.content_sha256 ?? undefined,
  };
}

function attorneyPackageFromWire(wire: WireAttorneyPackage | null): AttorneyPackage {
  if (wire === null) return {status: 'not_created'};
  return {
    status: wire.status,
    rowVersion: wire.version,
    // Points at the real, unauthenticated share-viewer page (FenzSafeShareView.tsx, wired in
    // main.tsx -- Issue #114, Slice C2), not the raw JSON API route directly: an attorney
    // following this link has no ProFenz session and must land on a real page, not a JSON blob.
    // The backend returns only the bare token, matching every other controller's "return the
    // data, not a pre-built URL" convention.
    shareLink: `${window.location.origin}/fenzsafe/share/${wire.share_token}`,
    createdAt: wire.created_at,
    openedAt: wire.opened_at ?? undefined,
    revokedAt: wire.revoked_at ?? undefined,
    sharedDocumentIds: wire.snapshot?.documents.map((document) => document.document_id),
  };
}

function evidenceRequestFromWire(wire: WireEvidenceRequest): EvidenceRequest {
  return {
    id: wire.request_id,
    description: wire.description,
    status: wire.status,
    fulfilledDocumentId: wire.fulfilled_document_id ?? undefined,
    declineReason: wire.decline_reason ?? undefined,
    createdAt: wire.created_at,
    resolvedAt: wire.resolved_at ?? undefined,
    version: wire.version,
  };
}

// Kept local to avoid the documented store/adapter mock module cycle. The mapped
// type requires every canonical MatterWatchTriggerType and rejects extra keys.
const watchTriggerTypes: Record<MatterWatchTriggerType, true> = {
  complaint: true, summons: true, subpoena: true, eviction_notice: true, domestication: true,
};

function matterWatchFromWire(wire: WireMatterWatchSubscription, matterId: string, expectedVersion?: number): MatterWatchSubscription {
  if (!wire || typeof wire !== 'object' || wire.matter_id !== matterId
    || (expectedVersion !== undefined && wire.version !== expectedVersion)
    || !Array.isArray(wire.party_names) || !wire.party_names.every((value) => typeof value === 'string')
    || !Array.isArray(wire.trigger_types) || !wire.trigger_types.every((value) => typeof value === 'string' && Object.hasOwn(watchTriggerTypes, value))
    || typeof wire.organization_id !== 'string' || wire.organization_id.length === 0
    || !(wire.case_type === null || typeof wire.case_type === 'string')
    || !(wire.case_number === null || typeof wire.case_number === 'string')
    || typeof wire.court !== 'string' || typeof wire.county !== 'string' || typeof wire.enabled !== 'boolean'
    || typeof wire.created_at !== 'string' || !Number.isInteger(wire.version) || wire.version < 1) {
    throw new FenzSafeBackendError('fenzsafe_matter_watch.invalid_response', 'Matter Watch could not be confirmed from the server response. Reload before taking another action.');
  }
  return {
    partyNames: wire.party_names,
    court: wire.court,
    county: wire.county,
    caseType: wire.case_type ?? undefined,
    caseNumber: wire.case_number ?? undefined,
    triggerTypes: wire.trigger_types,
    enabled: wire.enabled,
    createdAt: wire.created_at,
    version: wire.version,
  };
}

// Only an explicit 404 absence is authoritative. A failed read must not expose a new
// subscribe path or allow another mutation from stale cached absence.
const uncertainMatterWatches = new Set<string>();
const matterWatchEpochs = new Map<string, number>();
const pendingMatterWatchWrites = new Set<string>();
type MatterWatchRead = {watch: MatterWatchSubscription | null; generation: number; epoch: number};
function advanceMatterWatchEpoch(matterId: string): number {
  const next = (matterWatchEpochs.get(matterId) ?? 0) + 1;
  matterWatchEpochs.set(matterId, next);
  return next;
}
function assertCurrentWatchRead(matterId: string, read: MatterWatchRead): void {
  if (read.generation !== cacheGeneration) {
    throw new FenzSafeBackendError('fenzsafe_matter.stale_response', 'The response belonged to an earlier session. Reload before taking another action.');
  }
  if (read.epoch !== matterWatchEpochs.get(matterId)) {
    throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'Matter Watch changed while it was loading. Reload before taking another action.');
  }
}
async function fetchMatterWatch(matterId: string): Promise<MatterWatchRead> {
  const read: MatterWatchRead = {watch: null, generation: cacheGeneration, epoch: advanceMatterWatchEpoch(matterId)};
  try {
    const body = await request(`/api/v1/fenzsafe/matters/${matterId}/watch`, 'GET');
    read.watch = matterWatchFromWire(body.matter_watch_subscription as WireMatterWatchSubscription, matterId);
  } catch (error) {
    if (!(error instanceof FenzSafeBackendError && error.status === 404 && error.code === 'fenzsafe_matter_watch.not_found')) {
      if (read.generation === cacheGeneration && read.epoch === matterWatchEpochs.get(matterId)) uncertainMatterWatches.add(matterId);
      throw error;
    }
  }
  // A read that started before another read/write or unknown outcome cannot reconcile it.
  assertCurrentWatchRead(matterId, read);
  uncertainMatterWatches.delete(matterId);
  return read;
}

async function withMatterWatchWrite(matterId: string, action: () => Promise<Matter>): Promise<Matter> {
  const generation = cacheGeneration;
  if (!cache.has(matterId) || uncertainMatterWatches.has(matterId) || pendingMatterWatchWrites.has(matterId)) {
    throw new FenzSafeBackendError('fenzsafe_matter_watch.unconfirmed', 'Reload Matter Watch before taking another action; its current state could not be confirmed.');
  }
  pendingMatterWatchWrites.add(matterId);
  advanceMatterWatchEpoch(matterId);
  try {
    return await action();
  } catch (error) {
    if (generation === cacheGeneration) uncertainMatterWatches.add(matterId);
    throw error;
  } finally {
    if (generation === cacheGeneration) {
      // Also invalidate reads started during the request, before its eventual outcome.
      advanceMatterWatchEpoch(matterId);
      pendingMatterWatchWrites.delete(matterId);
    }
  }
}

/**
 * Degrades to an empty list rather than failing the whole matter load, same reasoning as
 * fetchTimeline() -- an environment where evidence requests aren't configured (feature not yet
 * live everywhere PROFENZ_FENZSAFE_ENABLED is) must not take the rest of the matter down with it.
 */
async function fetchEvidenceRequests(matterId: string): Promise<EvidenceRequest[]> {
  try {
    const body = await request(`/api/v1/fenzsafe/matters/${matterId}/evidence-requests`, 'GET');
    return (body.evidence_requests as WireEvidenceRequest[]).map(evidenceRequestFromWire);
  } catch {
    return [];
  }
}

function organizeSuggestionFromWire(wire: WireOrganizeSuggestion): OrganizeSuggestion {
  return {
    documentId: wire.document_id,
    currentCategory: wire.current_category as DocumentCategory,
    suggestedCategory: wire.suggested_category as DocumentCategory,
    reason: wire.reason,
  };
}

/**
 * Degrades to an empty list rather than failing the whole matter load, same reasoning as
 * fetchEvidenceRequests() -- DocumentController::organizeSuggestions() is purely computed and
 * read-only (Issue #192 Docket #17 Stage B), so a failure here means "show nothing to review yet,"
 * never "the matter itself couldn't load."
 */
async function fetchOrganizeSuggestions(matterId: string): Promise<OrganizeSuggestion[]> {
  try {
    const body = await request(`/api/v1/fenzsafe/matters/${matterId}/organize-suggestions`, 'GET');
    return (body.suggestions as WireOrganizeSuggestion[]).map(organizeSuggestionFromWire);
  } catch {
    return [];
  }
}

async function loadDocumentsAndPackage(matterId: string): Promise<{documents: FenzSafeDocument[]; attorneyPackage: AttorneyPackage}> {
  const [documentsBody, packageBody] = await Promise.all([
    request(`/api/v1/fenzsafe/matters/${matterId}/documents`, 'GET'),
    request(`/api/v1/fenzsafe/matters/${matterId}/attorney-package`, 'GET').catch((error: unknown) => {
      if (error instanceof FenzSafeBackendError && error.code === 'fenzsafe_attorney_package.not_found') return {attorney_package: null};
      throw error;
    }),
  ]);
  const documents = (documentsBody.documents as WireDocument[]).map(documentFromWire);
  const attorneyPackage = attorneyPackageFromWire((packageBody.attorney_package as WireAttorneyPackage | null) ?? null);
  return {documents, attorneyPackage};
}

async function composeMatter(wire: WireMatter): Promise<Matter> {
  const [{documents, attorneyPackage}, timeline, evidenceRequests, organizeSuggestions, watchRead] = await Promise.all([
    loadDocumentsAndPackage(wire.matter_id),
    fetchTimeline(wire.matter_id),
    fetchEvidenceRequests(wire.matter_id),
    fetchOrganizeSuggestions(wire.matter_id),
    fetchMatterWatch(wire.matter_id),
  ]);
  assertCurrentWatchRead(wire.matter_id, watchRead);
  const matterWatch = watchRead.watch;
  return {
    id: wire.matter_id,
    name: wire.name,
    reference: wire.reference,
    createdAt: wire.created_at,
    tier: wire.tier,
    archived: wire.archived,
    documents,
    timeline,
    attorneyPackage,
    evidenceRequests,
    matterWatch,
    organizeSuggestions,
    notifyOnAttorneyOpen: wire.notify_on_attorney_open,
    userRole: wire.user_role,
    representationStatus: normalizeRepresentationStatus(wire.representation_status),
    version: wire.version,
  };
}

function normalizeRepresentationStatus(value: WireMatter['representation_status']): FenzSafeRepresentationStatus | null {
  if (value === undefined || value === null) return null;
  if (value === 'represented' || value === 'seeking_counsel' || value === 'self_represented') return value;
  throw new FenzSafeBackendError('fenzsafe_matter.invalid_response', 'The matter representation status could not be read safely.');
}

let cache = new Map<string, Matter>();
// useSyncExternalStore requires getSnapshot() to return a referentially stable value when
// nothing has changed -- a fresh {matters: [...]} object on every call (even an identical one)
// makes React conclude the store changed on every render and loop forever ("Maximum update
// depth exceeded"). `snapshot` is only replaced here, in commit(), alongside a real cache change.
let snapshot: FenzSafeState = {matters: []};
let loadError: FenzSafeLoadError | undefined;
let creationSummary: FenzSafeMatterCreationSummary | undefined;
// Every account/scope reload invalidates narrow in-flight cache patches. A stale package GET
// must never repopulate the cache after sign-out, account switching, or another reset.
let cacheGeneration = 0;
const listeners = new Set<() => void>();

function commit() {
  snapshot = {matters: Array.from(cache.values()), loadError, creationSummary};
  for (const listener of listeners) listener();
}

/**
 * A genuinely absent session (DocumentController/MatterController/AttorneyPackageController's own
 * FenzSafeSignInRequired -> 401 fenzsafe_*.sign_in_required) is the one load failure worth telling
 * apart from every other kind -- it has a real fix (sign in again) that a generic "try again"
 * doesn't. Everything else (a network failure, a still-valid-but-denied session, a 5xx) collapses
 * to 'unavailable', matching FenzSafeWorkspace.tsx's own two-branch recovery UI.
 */
function classifyLoadError(error: unknown): FenzSafeLoadError {
  if (error instanceof FenzSafeBackendError && error.code.endsWith('.sign_in_required')) {
    return {type: 'sign_in_required', message: error.message};
  }
  return {type: 'unavailable', message: error instanceof Error ? error.message : 'FenzSafe could not be loaded. Try again.'};
}

function creationSummaryFromWire(value: unknown, usedFallback: number): FenzSafeMatterCreationSummary {
  if (typeof value !== 'object' || value === null) return {available: false, used: usedFallback, limit: null, reason: 'policy_unavailable'};
  const candidate = value as Partial<FenzSafeMatterCreationSummary>;
  const validReason = candidate.reason === 'available' || candidate.reason === 'included_matter_used' || candidate.reason === 'policy_unavailable';
  const validLimit = candidate.limit === null || (Number.isInteger(candidate.limit) && (candidate.limit as number) >= 1);
  if (typeof candidate.available !== 'boolean' || !Number.isInteger(candidate.used) || (candidate.used as number) < 0 || !validLimit || !validReason) {
    return {available: false, used: usedFallback, limit: null, reason: 'policy_unavailable'};
  }
  const used = candidate.used as number;
  const limit = candidate.limit as number | null;
  const consistent = candidate.reason === 'policy_unavailable'
    ? !candidate.available && limit === null
    : limit !== null && candidate.available === (used < limit) && candidate.reason === (candidate.available ? 'available' : 'included_matter_used');
  if (!consistent) return {available: false, used: usedFallback, limit: null, reason: 'policy_unavailable'};
  return candidate as FenzSafeMatterCreationSummary;
}

/**
 * Never lets a load failure become an unhandled promise rejection (this used to be the case: the
 * only caller, reset(), fires this with `void`, so a thrown error here previously vanished
 * silently, leaving the UI showing an empty "create your first matter" onboarding screen with no
 * indication anything went wrong -- indistinguishable from a genuinely new account, including for
 * a customer who was simply signed out). Every outcome -- success or failure -- ends in commit(),
 * so a subscriber always sees either the real matters or a real, classified loadError, never a
 * stale "still loading" state forever.
 */
async function refreshAll(): Promise<void> {
  const generation = cacheGeneration;
  try {
    const body = await request('/api/v1/fenzsafe/matters', 'GET');
    const wireMatters = [...(body.active as WireMatter[]), ...(body.archived as WireMatter[])];
    if (generation !== cacheGeneration) return;
    const pending = wireMatters.map(composeMatter);
    const epochs = new Map(matterWatchEpochs);
    const composed = await Promise.all(pending);
    if (generation !== cacheGeneration) return;
    for (const matter of composed) assertCurrentWatchRead(matter.id, {watch: matter.matterWatch, generation, epoch: epochs.get(matter.id)!});
    cache = new Map(composed.map((matter) => [matter.id, matter]));
    creationSummary = creationSummaryFromWire(body.creation, wireMatters.length);
    loadError = undefined;
  } catch (error) {
    if (generation !== cacheGeneration) return;
    // A superseded watch read cannot erase newer scoped cache state either.
    if (!(error instanceof FenzSafeBackendError && error.code === 'fenzsafe_matter_watch.stale_response')) {
      cache = new Map();
      creationSummary = undefined;
    }
    loadError = classifyLoadError(error);
  }
  commit();
}

async function refreshOne(matterId: string, wire: WireMatter, expectedGeneration?: number): Promise<Matter> {
  if (expectedGeneration !== undefined && expectedGeneration !== cacheGeneration) {
    throw new FenzSafeBackendError('fenzsafe_matter.stale_response', 'The saved response belonged to an earlier session. Reload before trying again.');
  }
  const matter = await composeMatter(wire);
  if (expectedGeneration !== undefined && expectedGeneration !== cacheGeneration) {
    throw new FenzSafeBackendError('fenzsafe_matter.stale_response', 'The saved response belonged to an earlier session. Reload before trying again.');
  }
  const current = cache.get(matterId);
  if (current && current.version > matter.version) return current;
  cache.set(matterId, matter);
  commit();
  return matter;
}

export const fenzsafeBackendAdapter = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  getSnapshot(): FenzSafeState {
    return snapshot;
  },
  reset() {
    cacheGeneration += 1;
    uncertainMatterWatches.clear();
    matterWatchEpochs.clear();
    pendingMatterWatchWrites.clear();
    cache = new Map();
    loadError = undefined;
    creationSummary = undefined;
    commit();
    void refreshAll();
  },

  createMatter(name: string, reference: string, representationStatus: FenzSafeRepresentationStatus | null, idempotencyKey: string, expectedScope?: string): Promise<string> {
    return (async () => {
      const body = await request('/api/v1/fenzsafe/matters', 'POST', {name, reference, representation_status: representationStatus, idempotency_key: idempotencyKey}, expectedScope);
      const wire = body.matter as WireMatter;
      const matter = await composeMatter(wire);
      cache.set(matter.id, matter);
      if (creationSummary && body.created === true) {
        const used = creationSummary.used + 1;
        if (creationSummary.limit === null) {
          creationSummary = {...creationSummary, used};
        } else {
          const available = used < creationSummary.limit;
          creationSummary = {...creationSummary, used, available, reason: available ? 'available' : 'included_matter_used'};
        }
      }
      commit();
      return matter.id;
    })();
  },

  addDocument(
    matterId: string,
    expectedVersion: number,
    input: {name: string; category: DocumentCategory; source: 'upload' | 'phone_import' | 'desktop_discovery'; sizeKb: number; phoneSource?: string},
    idempotencyKey: string,
    expectedScope?: string,
  ): Promise<Matter> {
    void idempotencyKey; // FenzSafeDocumentRepository::addDocument() takes no idempotency parameter.
    void expectedVersion; // A document's own version, not the matter's, guards this mutation server-side (see FenzSafeDocumentRepository) -- the matter's version is untouched by document changes.
    return (async () => {
      await request(`/api/v1/fenzsafe/matters/${matterId}/documents`, 'POST', {
        name: input.name,
        category: input.category,
        source: input.source,
        size_kb: input.sizeKb,
        phone_source: input.phoneSource,
      }, expectedScope);
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  /**
   * Sends the file's actual bytes to DocumentController::registerBytes() -- raw body, the file's
   * own content type, not JSON (that controller rejects a JSON body outright; see its own doc
   * comment). No FileReader copy is made -- the browser streams the File directly as the fetch
   * body. The response already carries the document's post-scan state (accepted/quarantined) or
   * throws a FenzSafeBackendError for a 503 scanner/storage-unavailable outcome; refreshing after
   * either lets the UI show whichever real state resulted, never assumed.
   */
  registerDocumentBytes(matterId: string, expectedVersion: number, documentId: string, file: File, idempotencyKey: string, expectedScope?: string): Promise<Matter> {
    void expectedVersion; // Same aggregate-boundary reasoning as addDocument() above -- a document's own version guards this server-side, not the matter's.
    void idempotencyKey; // Content-derived idempotency (documentId + sha256 of the bytes) is already handled server-side -- see DocumentController::registerBytes()'s own doc comment.
    return (async () => {
      const response = await fetch(`/api/v1/fenzsafe/documents/${documentId}/bytes`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {'Accept': 'application/json', 'Content-Type': file.type || 'application/octet-stream', 'X-ProFenz-CSRF': csrfToken(), ...scopeHeader(expectedScope)},
        body: file,
      });
      const parsed = (await response.json()) as Record<string, unknown>;
      if (!response.ok) {
        const code = typeof parsed.code === 'string' ? parsed.code : 'fenzsafe.unknown_error';
        const message = typeof parsed.message === 'string' ? parsed.message : 'The document bytes could not be registered.';
        if (code.endsWith('.conflict') || code.endsWith('.superseded')) throw new FenzSafeConflictError(message);
        throw new FenzSafeBackendError(code, message);
      }
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  /** Downloads a document's real, already-accepted bytes via DocumentController::downloadBytes(). */
  downloadDocumentBytes(documentId: string, signal?: AbortSignal): Promise<Blob> {
    return (async () => {
      const response = await fetch(`/api/v1/fenzsafe/documents/${documentId}/bytes`, {
        method: 'GET',
        signal,
        credentials: 'same-origin',
        headers: {'X-ProFenz-CSRF': csrfToken()},
      });
      if (!response.ok) {
        let code = 'fenzsafe.unknown_error';
        let message = 'The document could not be downloaded.';
        try {
          const parsed = (await response.json()) as Record<string, unknown>;
          if (typeof parsed.code === 'string') code = parsed.code;
          if (typeof parsed.message === 'string') message = parsed.message;
        } catch {
          // downloadBytes() only ever returns JSON on its error paths -- an unparseable body here means something else in front of the app (a proxy, a network error page), not a FenzSafe error code.
        }
        throw new FenzSafeBackendError(code, message);
      }
      return response.blob();
    })();
  },

  /** Reloads the current matter row plus its related resources for truthful conflict recovery. */
  refreshMatter(matterId: string): Promise<Matter> {
    return refreshMatterFromServer(matterId);
  },

  addDocumentVersion(matterId: string, documentId: string, expectedVersion: number, input: {name: string; sizeKb: number}, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      await request(`/api/v1/fenzsafe/documents/${documentId}/versions`, 'POST', {
        expected_version: expectedVersion,
        name: input.name,
        size_kb: input.sizeKb,
      });
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  updateDocument(
    matterId: string,
    documentId: string,
    expectedVersion: number,
    patch: Partial<Pick<FenzSafeDocument, 'name' | 'category' | 'triageState' | 'evidenceRelevance'>>,
    idempotencyKey: string,
  ): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      // DocumentController::update() has no name field -- see class doc comment above. A `name`
      // patch is dropped rather than silently failing the whole request.
      const body: Record<string, unknown> = {expected_version: expectedVersion};
      if (patch.category !== undefined) body.category = patch.category;
      if (patch.triageState !== undefined) body.triage_state = patch.triageState;
      if (patch.evidenceRelevance !== undefined) body.evidence_relevance = patch.evidenceRelevance;
      await request(`/api/v1/fenzsafe/documents/${documentId}`, 'PATCH', body);
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  removeDocument(matterId: string, documentId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      await request(`/api/v1/fenzsafe/documents/${documentId}`, 'DELETE', {expected_version: expectedVersion});
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  renameMatter(matterId: string, expectedVersion: number, input: {name: string; reference: string}, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/rename`, 'POST', {
        expected_version: expectedVersion,
        name: input.name,
        reference: input.reference,
      });
      return refreshOne(matterId, body.matter as WireMatter);
    })();
  },

  createAttorneyPackage(matterId: string, expectedVersion: number, documentIds: string[], idempotencyKey: string): Promise<Matter> {
    void idempotencyKey; // AttorneyPackageRepository::create() takes no idempotency parameter, and no expected_version either -- see its interface signature.
    void expectedVersion;
    return (async () => {
      await request(`/api/v1/fenzsafe/matters/${matterId}/attorney-package`, 'POST', {document_ids: documentIds});
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  simulateAttorneyOpen(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    void expectedVersion;
    // No backend equivalent exists -- opening a share link is a real unauthenticated GET against
    // AttorneyPackageController::view(), not something the authenticated customer session can
    // trigger on demand. Left as a client-only simulation of the status flip; unlike before
    // (Issue #114 Task 2), it no longer fabricates a matching timeline entry -- the timeline is
    // now the real, durable one fetched from the backend, and a client-only simulation has no
    // real fenzsafe_attorney_package.share_link_viewed event behind it. A genuine attorney open
    // now appears in the timeline on its own, sourced from that real event, the next time this
    // matter is refreshed.
    return (async () => {
      const current = cache.get(matterId);
      if (!current) throw new Error(`Unknown matter: ${matterId}`);
      if (current.attorneyPackage.status !== 'shared' && current.attorneyPackage.status !== 'opened') {
        throw new Error('No attorney package has been shared yet.');
      }
      const next: Matter = {
        ...current,
        attorneyPackage: {...current.attorneyPackage, status: 'opened', openedAt: 'Just now'},
      };
      cache.set(matterId, next);
      commit();
      return next;
    })();
  },

  revokeAttorneyPackage(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      const generation = cacheGeneration;
      try {
        await request(`/api/v1/fenzsafe/matters/${matterId}/attorney-package/revoke`, 'POST', {expected_version: expectedVersion});
      } catch (error) {
        if (!(error instanceof FenzSafeConflictError) || error.code !== 'fenzsafe_attorney_package.conflict') throw error;

        if (generation !== cacheGeneration) {
          throw new FenzSafeBackendError(
            'fenzsafe_attorney_package.refresh_failed',
            'Access was not revoked, and the latest package could not be safely loaded. Reload before trying again.',
          );
        }
        let refreshed: AttorneyPackage;
        try {
          refreshed = await refreshAttorneyPackageAfterRevokeConflict(matterId, generation);
        } catch {
          throw new FenzSafeBackendError(
            'fenzsafe_attorney_package.refresh_failed',
            'Access was not revoked, and the latest package could not be safely loaded. Reload before trying again.',
          );
        }
        if (refreshed.status === 'revoked') {
          throw new FenzSafeConflictError('Access was not revoked by this attempt. The latest package is now shown and was already revoked by another update.', error.code);
        }
        throw new FenzSafeConflictError('Access was not revoked. The shared package changed, and its latest state is now shown. Review it before trying again.', error.code);
      }
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  /** AttorneyPackageController::updateSnapshot() -- replaces membership without touching the share token/status, so no expected_version is needed (the package's own version field is untouched by this call). */
  updateAttorneyPackageSnapshot(matterId: string, expectedVersion: number, documentIds: string[], idempotencyKey: string): Promise<Matter> {
    void expectedVersion;
    void idempotencyKey;
    return (async () => {
      await request(`/api/v1/fenzsafe/matters/${matterId}/attorney-package/snapshot`, 'POST', {document_ids: documentIds});
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  updateNotificationPreference(matterId: string, expectedVersion: number, notifyOnAttorneyOpen: boolean, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/notification-preference`, 'PATCH', {
        expected_version: expectedVersion,
        notify_on_attorney_open: notifyOnAttorneyOpen,
      });
      return refreshOne(matterId, body.matter as WireMatter);
    })();
  },

  updateRepresentationStatus(matterId: string, expectedVersion: number, representationStatus: FenzSafeRepresentationStatus | null, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      const generation = cacheGeneration;
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/representation-status`, 'POST', {
        expected_version: expectedVersion,
        representation_status: representationStatus,
      });
      return refreshOne(matterId, body.matter as WireMatter, generation);
    })();
  },

  upgradeTier(matterId: string, expectedVersion: number, tier: MatterTier, idempotencyKey: string): Promise<Matter> {
    return (async () => {
      const {amountMinor, currencyCode} = await activePricingPlanTerms(tier);
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/upgrade-tier`, 'POST', {
        expected_version: expectedVersion,
        to_tier: tier,
        amount_minor: amountMinor,
        currency_code: currencyCode,
        idempotency_key: idempotencyKey,
      });
      return refreshOne(matterId, body.matter as WireMatter);
    })();
  },

  archiveMatter(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/archive`, 'POST', {expected_version: expectedVersion});
      return refreshOne(matterId, body.matter as WireMatter);
    })();
  },

  restoreMatter(matterId: string, expectedVersion: number, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/restore`, 'POST', {expected_version: expectedVersion});
      return refreshOne(matterId, body.matter as WireMatter);
    })();
  },

  fulfillEvidenceRequest(matterId: string, requestId: string, requestExpectedVersion: number, documentId: string, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      await request(`/api/v1/fenzsafe/evidence-requests/${requestId}/fulfill`, 'POST', {expected_version: requestExpectedVersion, document_id: documentId});
      // EvidenceRequestController::fulfill() returns only the updated request, not a full
      // WireMatter -- same reason updateSnapshot() above re-composes the whole matter rather
      // than trying to patch the cache locally.
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  declineEvidenceRequest(matterId: string, requestId: string, requestExpectedVersion: number, reason: string | undefined, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey;
    return (async () => {
      await request(`/api/v1/fenzsafe/evidence-requests/${requestId}/decline`, 'POST', {expected_version: requestExpectedVersion, reason: reason ?? null});
      return refreshMatterAfterDocumentChange(matterId);
    })();
  },

  subscribeMatterWatch(matterId, input, idempotencyKey) {
    void idempotencyKey; // MatterWatchController::subscribe() has no idempotency handling; a genuine retry surfaces as fenzsafe_matter_watch.conflict, same as any other double-submit.
    return withMatterWatchWrite(matterId, async () => {
      const generation = cacheGeneration;
      const watchBeforeWrite = cache.get(matterId)?.matterWatch ?? null;
      if (watchBeforeWrite) throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'A subscription is already present. Reload before taking another action.');
      const body = await request(`/api/v1/fenzsafe/matters/${matterId}/watch`, 'POST', {
        party_names: input.partyNames,
        court: input.court,
        county: input.county,
        case_type: input.caseType ?? null,
        case_number: input.caseNumber ?? null,
        trigger_types: input.triggerTypes,
      });
      return applyMatterWatchWrite(matterId, generation, watchBeforeWrite, matterWatchFromWire(body.matter_watch_subscription as WireMatterWatchSubscription, matterId, 1));
    });
  },

  updateMatterWatch(matterId, watchExpectedVersion, patch, idempotencyKey) {
    void idempotencyKey;
    return withMatterWatchWrite(matterId, async () => {
      const generation = cacheGeneration;
      const watchBeforeWrite = cache.get(matterId)?.matterWatch ?? null;
      if (!watchBeforeWrite || watchBeforeWrite.version !== watchExpectedVersion) {
        throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'Matter Watch changed. Reload before taking another action.');
      }
      const body: Record<string, unknown> = {expected_version: watchExpectedVersion};
      if (patch.partyNames !== undefined) body.party_names = patch.partyNames;
      if (patch.court !== undefined) body.court = patch.court;
      if (patch.county !== undefined) body.county = patch.county;
      if (patch.caseType !== undefined) body.case_type = patch.caseType;
      if (patch.caseNumber !== undefined) body.case_number = patch.caseNumber;
      if (patch.triggerTypes !== undefined) body.trigger_types = patch.triggerTypes;
      if (patch.enabled !== undefined) body.enabled = patch.enabled;
      const response = await request(`/api/v1/fenzsafe/matters/${matterId}/watch`, 'PATCH', body);
      return applyMatterWatchWrite(matterId, generation, watchBeforeWrite, matterWatchFromWire(response.matter_watch_subscription as WireMatterWatchSubscription, matterId, watchExpectedVersion + 1));
    });
  },

  unsubscribeMatterWatch(matterId, watchExpectedVersion, idempotencyKey) {
    void idempotencyKey;
    return withMatterWatchWrite(matterId, async () => {
      const generation = cacheGeneration;
      const watchBeforeWrite = cache.get(matterId)?.matterWatch ?? null;
      if (!watchBeforeWrite || watchBeforeWrite.version !== watchExpectedVersion) {
        throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'Matter Watch changed. Reload before taking another action.');
      }
      const response = await request(`/api/v1/fenzsafe/matters/${matterId}/watch`, 'DELETE', {expected_version: watchExpectedVersion});
      if (response.unsubscribed !== true) {
        throw new FenzSafeBackendError('fenzsafe_matter_watch.invalid_response', 'Matter Watch may have changed, but the server receipt was not valid. Reload before taking another action.');
      }
      return applyMatterWatchWrite(matterId, generation, watchBeforeWrite, null);
    });
  },

  completeOrganizeSuggestions(matterId: string, accepted: number, edited: number, skipped: number, idempotencyKey: string): Promise<Matter> {
    void idempotencyKey; // See class doc comment #2 -- this endpoint has nowhere to put one yet.
    return (async () => {
      await request(`/api/v1/fenzsafe/matters/${matterId}/organize-suggestions/complete`, 'POST', {accepted, edited, skipped});
      // The write above already durably committed -- a failure past this point (the follow-up
      // GET) must never look like the whole call failed, or a caller's "Try again" would resend
      // {accepted, edited, skipped} and double-count them against an endpoint with no idempotency
      // handling (Codex STOP/CORRECT review 5327607777). Surface it as its own distinguishable code
      // so the caller can treat the write as confirmed and only retry the read, or skip retrying at
      // all, rather than replaying the mutation.
      try {
        return await refreshMatterAfterDocumentChange(matterId);
      } catch (refreshError) {
        throw new FenzSafeBackendError(
          'fenzsafe_organize_suggestions.refresh_failed',
          refreshError instanceof Error ? refreshError.message : 'The review was saved, but the latest list could not be reloaded.',
        );
      }
    })();
  },
} satisfies FenzSafeAdapter;

/**
 * Applies only the mutation receipt's Matter Watch field to the latest same-session matter.
 * Unlike the historical broad refresh, this cannot repopulate a reset cache or overwrite newer
 * document/package/timeline state while unrelated reads are in flight.
 */
function applyMatterWatchWrite(
  matterId: string,
  generation: number,
  watchBeforeWrite: MatterWatchSubscription | null,
  matterWatch: MatterWatchSubscription | null,
): Matter {
  if (generation !== cacheGeneration) {
    throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'The FenzSafe account changed while Matter Watch was saving. Reload before taking another action.');
  }
  const current = cache.get(matterId);
  if (!current) {
    throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'The matter is no longer available in this FenzSafe scope.');
  }
  // Equivalent refresh objects still describe the same subscription/version. A newer
  // or different subscription must not be removed or reported as the old operation's result.
  const sameWatch = (left: MatterWatchSubscription | null, right: MatterWatchSubscription | null) => JSON.stringify(left) === JSON.stringify(right);
  if (!sameWatch(current.matterWatch, watchBeforeWrite)) {
    if (sameWatch(current.matterWatch, matterWatch)) return current;
    throw new FenzSafeBackendError('fenzsafe_matter_watch.stale_response', 'Matter Watch changed while the request was completing. Reload to confirm the current subscription before taking another action.');
  }
  const next = {...current, matterWatch};
  cache.set(matterId, next);
  commit();
  return next;
}

/**
 * Reloads the authoritative matter row as well as its related resources. There is no single-item
 * matter endpoint, so conflict recovery reads the tenant-scoped list and selects the requested
 * matter. The generation is carried across the list request and every compose await; an old
 * account can therefore never repopulate the cache after reset().
 */
async function refreshMatterFromServer(matterId: string): Promise<Matter> {
  const generation = cacheGeneration;
  const body = await request('/api/v1/fenzsafe/matters', 'GET');
  if (generation !== cacheGeneration) {
    throw new FenzSafeBackendError('fenzsafe_matter.stale_response', 'The matter belonged to an earlier session. Reload before trying again.');
  }
  const wire = [...(body.active as WireMatter[]), ...(body.archived as WireMatter[])].find((candidate) => candidate.matter_id === matterId);
  if (!wire) throw new FenzSafeBackendError('fenzsafe_matter.not_found', 'The matter could not be found in the current account.');
  return refreshOne(matterId, wire, generation);
}

/**
 * DocumentController/AttorneyPackageController mutations return the changed document/package,
 * not the parent Matter (there is no "refresh this one matter" matter endpoint -- MatterController
 * has no find() action, see docs/ai/FENZSAFE_HTTP_INTEGRATION.md's "not a gap" note). The matter's
 * own version does not advance when only its documents/attorney-package change (they carry their
 * own version fields), so the cached matter's other fields are reused as-is and only the
 * documents/attorneyPackage/timeline are recomposed.
 */
async function refreshMatterAfterDocumentChange(matterId: string): Promise<Matter> {
  const current = cache.get(matterId);
  if (!current) throw new Error(`Unknown matter: ${matterId}`);
  const [{documents, attorneyPackage}, timeline, evidenceRequests, organizeSuggestions, watchRead] = await Promise.all([
    loadDocumentsAndPackage(matterId),
    fetchTimeline(matterId),
    fetchEvidenceRequests(matterId),
    fetchOrganizeSuggestions(matterId),
    fetchMatterWatch(matterId),
  ]);
  assertCurrentWatchRead(matterId, watchRead);
  const matterWatch = watchRead.watch;
  const next: Matter = {...current, documents, attorneyPackage, timeline, evidenceRequests, organizeSuggestions, matterWatch};
  cache.set(matterId, next);
  commit();
  return next;
}

/**
 * Narrow recovery for a stale revoke. Only the owner-scoped package is fetched and only that
 * field is patched onto the matter as it exists when the response arrives. This deliberately
 * avoids refreshMatterAfterDocumentChange(): that broad helper captures a whole matter before
 * awaiting and could overwrite unrelated local changes with an older object.
 */
async function refreshAttorneyPackageAfterRevokeConflict(matterId: string, generation: number): Promise<AttorneyPackage> {
  const packageBeforeRefresh = cache.get(matterId)?.attorneyPackage;
  if (!packageBeforeRefresh) throw new Error('The matter is no longer available in this FenzSafe scope.');
  const body = await request(`/api/v1/fenzsafe/matters/${matterId}/attorney-package`, 'GET');
  const refreshed = attorneyPackageFromWire((body.attorney_package as WireAttorneyPackage | null) ?? null);

  if (generation !== cacheGeneration) throw new Error('The FenzSafe account scope changed while the package was loading.');
  const current = cache.get(matterId);
  if (!current) throw new Error('The matter is no longer available in this FenzSafe scope.');

  const currentVersion = current.attorneyPackage.rowVersion ?? 0;
  const refreshedVersion = refreshed.rowVersion ?? 0;
  if (currentVersion > refreshedVersion || (currentVersion === refreshedVersion && current.attorneyPackage !== packageBeforeRefresh)) {
    return current.attorneyPackage;
  }

  cache.set(matterId, {...current, attorneyPackage: refreshed});
  commit();
  return refreshed;
}
